/** Text-to-speech via the browser's speechSynthesis — $0, offline, no key.
 *
 * The quality of this is entirely down to WHICH voice gets picked. Every
 * modern browser ships two very different classes of voice under the same
 * API: old formant synthesizers (eSpeak, "compact" voices — the robot the
 * client heard) and modern neural ones (Microsoft "Natural"/"Online",
 * Google, Apple "Enhanced"/"Premium"). Taking the first voice whose lang
 * starts with "de" picks between them at random. `rankVoice` below sorts
 * them so the best installed voice wins, and the learner can override the
 * choice and have it remembered.
 *
 * Two browser quirks are handled here because they both read as "the
 * speaking partner froze":
 *   - getVoices() is EMPTY on first call in Chrome until the voice list
 *     loads asynchronously, so an early call silently gets the default
 *     robot. We warm the list and re-read it on `voiceschanged`.
 *   - Chrome cuts an utterance off after ~15s and then never fires `end`,
 *     so the caller waiting on onEnd (the mic re-arm) waits forever. The
 *     pause/resume keep-alive below is the standard workaround.
 *
 * A paid-TTS upgrade path swaps THIS module only.
 */

const PREFERRED_VOICE_KEY = "discentem.voice";

export interface SpeakOptions {
  lang?: string;
  /** Playback speed (SpeechSynthesis rate). Defaults to 0.9 — slightly
   * slow, for learners rather than natives. */
  rate?: number;
  /** Fires when the utterance finishes (not when cancelled by a newer
   * one) — the speaking partner uses this to re-arm the microphone. */
  onEnd?: () => void;
  /** Fires as the voice advances, with progress as a 0..1 fraction of the
   * text — the audio player drives its progress bar from this. */
  onBoundary?: (fraction: number) => void;
}

export function ttsAvailable(): boolean {
  // The VALUE, not just the key: a browser that declares speechSynthesis and
  // leaves it undefined would otherwise pass this check and crash on use.
  return typeof window !== "undefined" && !!window.speechSynthesis;
}

/** How good a voice is likely to sound, higher is better.
 *
 * Named markers, not a hard-coded voice list: the exact names differ per
 * OS and change between releases, but the vendors have been consistent
 * about labelling their neural voices. A negative score is still usable —
 * it just loses to anything better on the machine. */
export function rankVoice(voice: SpeechSynthesisVoice): number {
  const name = `${voice.name} ${voice.voiceURI}`.toLowerCase();
  let score = 0;
  // Microsoft's neural voices (Edge, Windows 11) — the best free German
  // voices most learners will have: "Katja Online (Natural)", "Conrad".
  if (name.includes("natural") || name.includes("neural")) score += 100;
  if (name.includes("online")) score += 40;
  // Apple's better voices, offered as an opt-in download on iOS/macOS.
  if (name.includes("enhanced") || name.includes("premium")) score += 80;
  // Google's German voice (Chrome on Android/desktop) — clearly better
  // than the formant fallbacks, clearly below the neural ones.
  if (name.includes("google")) score += 50;
  // The robots. eSpeak and the "compact" voices are the ones that sound
  // synthetic; push them below everything else rather than banning them,
  // since on some Linux boxes they are all there is.
  if (name.includes("espeak") || name.includes("compact")) score -= 100;
  // A tie-break, not a preference: a local voice keeps working offline.
  if (voice.localService) score += 5;
  return score;
}

/** Every installed voice for `lang` (a prefix match, so "de" covers
 *  de-DE and de-AT), best first. Empty before the browser has loaded its
 *  voice list, which is why callers must tolerate an empty array. */
export function voicesFor(lang = "de"): SpeechSynthesisVoice[] {
  if (!ttsAvailable()) return [];
  const prefix = lang.slice(0, 2).toLowerCase();
  return window.speechSynthesis
    .getVoices()
    .filter((v) => v.lang.toLowerCase().startsWith(prefix))
    .sort((a, b) => rankVoice(b) - rankVoice(a));
}

/** The learner's chosen voice URI, if they picked one. */
export function getPreferredVoice(): string | null {
  try {
    return localStorage.getItem(PREFERRED_VOICE_KEY);
  } catch {
    return null; // private mode / storage blocked — fall back to ranking
  }
}

/** Remember a voice choice; null clears it (back to "best available"). */
export function setPreferredVoice(voiceURI: string | null): void {
  try {
    if (voiceURI) localStorage.setItem(PREFERRED_VOICE_KEY, voiceURI);
    else localStorage.removeItem(PREFERRED_VOICE_KEY);
  } catch {
    // Nothing to do — the ranking still gives a good default this session.
  }
}

/** The voice to use for `lang`: the learner's pick when it is still
 *  installed, otherwise the best-ranked one. */
export function pickVoice(lang = "de"): SpeechSynthesisVoice | null {
  const candidates = voicesFor(lang);
  if (candidates.length === 0) return null;
  const preferred = getPreferredVoice();
  return candidates.find((v) => v.voiceURI === preferred) ?? candidates[0];
}

/** Ask the browser to load its voice list and call back when it has.
 *
 * Chrome populates voices asynchronously — the first getVoices() returns
 * []. Returns an unsubscribe so a component can drop the listener. */
export function onVoicesReady(cb: () => void): () => void {
  if (!ttsAvailable()) return () => undefined;
  window.speechSynthesis.getVoices(); // triggers the async load in Chrome
  const handler = () => cb();
  window.speechSynthesis.addEventListener?.("voiceschanged", handler);
  if (window.speechSynthesis.getVoices().length > 0) cb();
  return () => window.speechSynthesis.removeEventListener?.("voiceschanged", handler);
}

// --- Chrome's 15-second cut-off --------------------------------------------
// Chrome stops a long utterance partway and never fires `end`, which strands
// anything waiting on onEnd (for us: the microphone never re-arms, and the
// conversation looks frozen). Nudging pause/resume on a timer keeps the
// utterance alive. Cleared the moment speech really ends.
let keepAlive: ReturnType<typeof setInterval> | null = null;

// Cancelling an utterance fires `error: "interrupted"` on the OLD one. Only
// the utterance we started last may call back, or a superseded turn would
// re-arm the microphone underneath the current one.
let speechToken = 0;

function stopKeepAlive(): void {
  if (keepAlive !== null) {
    clearInterval(keepAlive);
    keepAlive = null;
  }
}

function startKeepAlive(): void {
  stopKeepAlive();
  keepAlive = setInterval(() => {
    if (!ttsAvailable() || !window.speechSynthesis.speaking) {
      stopKeepAlive();
      return;
    }
    window.speechSynthesis.pause();
    window.speechSynthesis.resume();
  }, 10_000);
}

/** Speak `text`. Cancels any ongoing utterance first (rapid taps must not
 * queue a backlog). No-ops where unsupported. */
export function speak(text: string, opts: SpeakOptions = {}): boolean {
  if (!ttsAvailable() || !text.trim()) return false;
  window.speechSynthesis.cancel();
  stopKeepAlive();
  const token = ++speechToken;
  const finished = () => {
    if (token !== speechToken) return; // superseded — not our turn any more
    stopKeepAlive();
    opts.onEnd?.();
  };
  const utterance = new SpeechSynthesisUtterance(text);
  const lang = opts.lang ?? "de-DE";
  utterance.lang = lang;
  utterance.rate = opts.rate ?? 0.9; // slightly slow — learners, not natives
  const voice = pickVoice(lang);
  if (voice) utterance.voice = voice;
  utterance.onend = finished;
  // A synthesis error must not strand the caller either: a "not-allowed" or
  // "synthesis-failed" error would otherwise leave the mic un-armed forever
  // and the conversation looking frozen.
  utterance.onerror = finished;
  if (opts.onBoundary) {
    const total = text.length || 1;
    utterance.onboundary = (e: SpeechSynthesisEvent) =>
      opts.onBoundary?.(Math.min(1, e.charIndex / total));
  }
  window.speechSynthesis.speak(utterance);
  startKeepAlive();
  return true;
}

/** Transport controls for the current utterance — no-ops where the
 * browser has no speechSynthesis. The audio player wires these to its
 * pause/resume/restart buttons. */
export function pauseSpeech(): void {
  if (ttsAvailable()) {
    stopKeepAlive(); // a deliberate pause must not be undone by the nudge
    window.speechSynthesis.pause();
  }
}

export function resumeSpeech(): void {
  if (ttsAvailable()) window.speechSynthesis.resume();
}

export function cancelSpeech(): void {
  if (ttsAvailable()) {
    speechToken += 1; // retire the current utterance's callbacks
    stopKeepAlive();
    window.speechSynthesis.cancel();
  }
}
